import type { MomentMessageType } from "../data/momentOptions";

export const MOMENT_SUMMARY_LABELS: Record<string, string> = {
  cost: "비용",
  msg_send: "발송수",
  msg_send_fail: "발송 실패",
  msg_open: "열람수",
  msg_click: "클릭수",
  msg_open_rate: "열람률",
  msg_click_rate: "클릭률",
  cost_per_msg_send: "발송당 비용",
  cost_per_msg_open: "열람당 비용",
  cost_per_msg_click: "클릭당 비용",
};

export function getMomentMetricLabel(key: string) {
  return MOMENT_SUMMARY_LABELS[key] ?? getMomentClickLabel(key);
}

export function getMomentClickLabel(key: string) {
  if (/msg_click_rate/i.test(key)) return "클릭률";
  if (/msg_open_rate/i.test(key)) return "열람률";
  if (/msg_click_button1/i.test(key)) return "버튼 1 클릭 수";
  if (/msg_click_button2/i.test(key)) return "버튼 2 클릭 수";
  if (/msg_click_share/i.test(key)) return "공유하기 클릭 수";
  if (/msg_click_coupon/i.test(key)) return "쿠폰 클릭 수";
  if (/msg_click_carousel_more/i.test(key)) return "더보기 클릭 수";
  if (/msg_click_more/i.test(key)) return "더보기 클릭 수";
  if (/msg_click_media/i.test(key)) return "이미지 클릭 수";
  if (/msg_click_title/i.test(key)) return "타이틀 클릭 수";
  if (/msg_click_text/i.test(key)) return "본문 클릭 수";
  const listMatch = key.match(/msg_click_list(\d+)/i);
  if (listMatch) return `리스트 ${listMatch[1]} 클릭 수`;
  const carouselButtonMatch = key.match(/msg_click_carousel_button(\d+)/i);
  if (carouselButtonMatch) return `카드 ${carouselButtonMatch[1]} 버튼1 클릭 수`;
  const carouselShareMatch = key.match(/msg_click_carousel_share(\d+)/i);
  if (carouselShareMatch) return `카드 ${carouselShareMatch[1]} 버튼2/공유 클릭 수`;
  const carouselCouponMatch = key.match(/msg_click_carousel(\d+)_coupon/i);
  if (carouselCouponMatch) return `카드 ${carouselCouponMatch[1]} 쿠폰 클릭 수`;
  const catalogMatch = key.match(/msg_click_catalog_item(\d+)/i);
  if (catalogMatch) return `카탈로그 ${catalogMatch[1]} 클릭 수`;
  return key;
}

export function formatMomentMetricCount(label: string, count: number) {
  if (label.endsWith("률")) {
    return `${count}%`;
  }
  return count.toLocaleString();
}

export function buildMomentDetailSections(
  messageType: MomentMessageType,
  messageBody: Record<string, unknown>,
  metricMap: Record<string, unknown>
) {
  const sections: Array<{ title: string; items: Array<{ label: string; count: number }> }> = [];
  const listCount = ensureArray(messageBody.items).length;
  const cardCount = ensureArray(messageBody.carousels).length;
  const buttonCount = ensureArray(messageBody.buttons).length;

  const pick = (keys: Array<{ key: string; label: string }>) =>
    keys
      .map((item) => ({ label: item.label, count: Number(metricMap[item.key] ?? 0) }))
      .filter((item) => !Number.isNaN(item.count));

  if (messageType === "BASIC_TEXT_MESSAGE") {
    sections.push({
      title: "텍스트 상세",
      items: pick([
        { key: "msg_click_button1", label: "버튼 1 클릭 수" },
        { key: "msg_click_button2", label: "버튼 2 클릭 수" },
        { key: "msg_click_coupon", label: "쿠폰 클릭 수" },
        { key: "msg_click_share", label: "공유하기 클릭 수" },
      ]),
    });
  }

  if (messageType === "WIDE_MESSAGE") {
    sections.push({
      title: "와이드 이미지 상세",
      items: pick([
        { key: "msg_click_media", label: "이미지 클릭 수" },
        { key: "msg_click_button1", label: "버튼 1 클릭 수" },
        { key: "msg_click_coupon", label: "쿠폰 클릭 수" },
        { key: "msg_click_share", label: "공유하기 클릭 수" },
      ]),
    });
  }

  if (messageType === "WIDE_LIST_MESSAGE") {
    sections.push({
      title: "와이드 리스트 상세",
      items: [
        ...Array.from({ length: listCount }, (_, index) => ({
          label: `리스트 ${index + 1} 클릭 수`,
          count: Number(metricMap[`msg_click_list${index + 1}`] ?? 0),
        })),
        ...pick([
          { key: "msg_click_button1", label: "버튼 1 클릭 수" },
          { key: "msg_click_coupon", label: "쿠폰 클릭 수" },
          { key: "msg_click_share", label: "공유하기 클릭 수" },
        ]),
      ],
    });
  }

  if (messageType === "CAROUSEL_FEED_MESSAGE" || messageType === "CAROUSEL_COMMERCE_MESSAGE") {
    const cardItems = Array.from({ length: cardCount }, (_, index) => {
      const cardIndex = index + 1;
      const carousel = ensureArray(messageBody.carousels)[index];
      const card = carousel && typeof carousel === "object" ? carousel as Record<string, unknown> : {};
      const buttons = ensureArray(card.buttons);
      const secondActionLabel = card.shareFlag ? "공유하기 클릭 수" : buttons.length > 1 ? "버튼2 클릭 수" : null;
      return [
        { label: `카드 ${cardIndex} 버튼1 클릭 수`, count: Number(metricMap[`msg_click_carousel_button${cardIndex}`] ?? 0) },
        { label: `카드 ${cardIndex} 쿠폰 클릭 수`, count: Number(metricMap[`msg_click_carousel${cardIndex}_coupon`] ?? 0) },
        ...(secondActionLabel
          ? [{ label: `카드 ${cardIndex} ${secondActionLabel}`, count: Number(metricMap[`msg_click_carousel_share${cardIndex}`] ?? 0) }]
          : []),
      ];
    }).flat();
    sections.push({
      title: messageType === "CAROUSEL_FEED_MESSAGE" ? "캐러셀 피드 상세" : "캐러셀 커머스 상세",
      items: [
        ...cardItems,
        { label: "더보기 클릭 수", count: Number(metricMap.msg_click_carousel_more ?? 0) },
      ],
    });
  }

  if (buttonCount > 0 && !["BASIC_TEXT_MESSAGE", "WIDE_MESSAGE", "WIDE_LIST_MESSAGE"].includes(messageType)) {
    sections.push({
      title: "버튼 상세",
      items: Array.from({ length: buttonCount }, (_, index) => ({
        label: `버튼 ${index + 1} 클릭 수`,
        count: Number(metricMap[`msg_click_button${index + 1}`] ?? 0),
      })),
    });
  }

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !Number.isNaN(item.count)),
    }))
    .filter((section) => section.items.length > 0);
}

export function buildMomentClickSummary(clickEntries: Array<[string, number]>) {
  return clickEntries
    .map(([key, count]) => ({
      key,
      label: getMomentMetricLabel(key),
      count,
    }))
    .sort((a, b) => compareMomentClickKey(a.key, b.key));
}

export function buildMomentStructuredClickSummary(
  messageType: MomentMessageType,
  messageBody: Record<string, unknown>,
  metricMap: Record<string, unknown>,
  includeRate = true
) {
  const items: Array<{ key: string; count: number }> = [];
  const buttons = ensureArray(messageBody.buttons);
  const listItems = ensureArray(messageBody.items);
  const carousels = ensureArray(messageBody.carousels);

  if (includeRate) {
    items.push({ key: "msg_click_rate", count: Number(metricMap.msg_click_rate ?? 0) });
  }

  if (messageType === "BASIC_TEXT_MESSAGE") {
    if (buttons.length > 0) {
      items.push({ key: "msg_click_button1", count: Number(metricMap.msg_click_button1 ?? 0) });
    }
    if (buttons.length > 1) {
      items.push({ key: "msg_click_button2", count: Number(metricMap.msg_click_button2 ?? 0) });
    }
    if (messageBody.couponBook) {
      items.push({ key: "msg_click_coupon", count: Number(metricMap.msg_click_coupon ?? 0) });
    }
    if (messageBody.shareFlag) {
      items.push({ key: "msg_click_share", count: resolveShareClickCount(messageType, metricMap, buttons.length) });
    }
  }

  if (messageType === "WIDE_MESSAGE") {
    items.push({ key: "msg_click_media", count: Number(metricMap.msg_click_media ?? 0) });
    if (buttons.length > 0) {
      items.push({ key: "msg_click_button1", count: Number(metricMap.msg_click_button1 ?? 0) });
    }
    if (messageBody.couponBook) {
      items.push({ key: "msg_click_coupon", count: Number(metricMap.msg_click_coupon ?? 0) });
    }
    if (messageBody.shareFlag) {
      items.push({ key: "msg_click_share", count: resolveShareClickCount(messageType, metricMap, buttons.length) });
    }
  }

  if (messageType === "WIDE_LIST_MESSAGE") {
    listItems.forEach((_, index) => {
      items.push({ key: `msg_click_list${index + 1}`, count: Number(metricMap[`msg_click_list${index + 1}`] ?? 0) });
    });
    if (buttons.length > 0) {
      items.push({ key: "msg_click_button1", count: Number(metricMap.msg_click_button1 ?? 0) });
    }
    if (messageBody.couponBook) {
      items.push({ key: "msg_click_coupon", count: Number(metricMap.msg_click_coupon ?? 0) });
    }
    if (messageBody.shareFlag) {
      items.push({ key: "msg_click_share", count: resolveShareClickCount(messageType, metricMap, buttons.length) });
    }
  }

  if (messageType === "CAROUSEL_FEED_MESSAGE" || messageType === "CAROUSEL_COMMERCE_MESSAGE") {
    if (messageType === "CAROUSEL_COMMERCE_MESSAGE" && messageBody.introCarousel) {
      items.push({ key: "msg_click_media", count: Number(metricMap.msg_click_media ?? 0) });
    }
    carousels.forEach((carousel, index) => {
      const cardIndex = index + 1;
      const card = carousel && typeof carousel === "object" ? carousel as Record<string, unknown> : {};
      const cardButtons = ensureArray(card.buttons);
      items.push({
        key: `msg_click_carousel_button${cardIndex}`,
        count: Number(metricMap[`msg_click_carousel_button${cardIndex}`] ?? 0),
      });
      if (card.couponBook) {
        items.push({
          key: `msg_click_carousel${cardIndex}_coupon`,
          count: Number(metricMap[`msg_click_carousel${cardIndex}_coupon`] ?? 0),
        });
      }
      if (card.shareFlag || cardButtons.length > 1) {
        items.push({
          key: `msg_click_carousel_share${cardIndex}`,
          count: Number(metricMap[`msg_click_carousel_share${cardIndex}`] ?? 0),
        });
      }
    });
    if (messageBody.moreButton) {
      items.push({ key: "msg_click_carousel_more", count: Number(metricMap.msg_click_carousel_more ?? 0) });
    }
  }

  const uniqueItems = items.filter((item, index, array) => array.findIndex((candidate) => candidate.key === item.key) === index);
  return uniqueItems
    .map((item) => ({
      ...item,
      label: getMomentStructuredClickLabel(messageType, messageBody, item.key),
    }))
    .sort((a, b) => compareMomentClickKey(a.key, b.key));
}

export function toMomentMetricMap(clickStats: Array<{ key: string; count: number }>) {
  return clickStats.reduce<Record<string, unknown>>((acc, item) => {
    acc[item.key] = item.count;
    return acc;
  }, {});
}

export function ensureArray(value: unknown) {
  return Array.isArray(value) ? value : [];
}

function getMomentStructuredClickLabel(
  messageType: MomentMessageType,
  messageBody: Record<string, unknown>,
  key: string
) {
  if (messageType === "CAROUSEL_COMMERCE_MESSAGE" && messageBody.introCarousel && /msg_click_media/i.test(key)) {
    return "인트로 이미지 클릭 수";
  }
  if (messageType === "CAROUSEL_FEED_MESSAGE" || messageType === "CAROUSEL_COMMERCE_MESSAGE") {
    const carousels = ensureArray(messageBody.carousels);
    const carouselButtonMatch = key.match(/msg_click_carousel_button(\d+)/i);
    if (carouselButtonMatch) {
      return `카드 ${carouselButtonMatch[1]} 버튼1 클릭 수`;
    }

    const carouselShareMatch = key.match(/msg_click_carousel_share(\d+)/i);
    if (carouselShareMatch) {
      const cardIndex = Number(carouselShareMatch[1]) - 1;
      const carousel = carousels[cardIndex];
      const card = carousel && typeof carousel === "object" ? carousel as Record<string, unknown> : {};
      const buttons = ensureArray(card.buttons);
      if (card.shareFlag) {
        return `카드 ${carouselShareMatch[1]} 공유하기 클릭 수`;
      }
      if (buttons.length > 1) {
        return `카드 ${carouselShareMatch[1]} 버튼2 클릭 수`;
      }
      return `카드 ${carouselShareMatch[1]} 버튼2/공유 클릭 수`;
    }

    const carouselCouponMatch = key.match(/msg_click_carousel(\d+)_coupon/i);
    if (carouselCouponMatch) {
      return `카드 ${carouselCouponMatch[1]} 쿠폰 클릭 수`;
    }
  }
  return getMomentMetricLabel(key);
}

function compareMomentClickKey(a: string, b: string) {
  const aRank = getMomentClickOrder(a);
  const bRank = getMomentClickOrder(b);

  if (aRank.group !== bRank.group) {
    return aRank.group - bRank.group;
  }
  if (aRank.index !== bRank.index) {
    return aRank.index - bRank.index;
  }
  if (aRank.subGroup !== bRank.subGroup) {
    return aRank.subGroup - bRank.subGroup;
  }
  return a.localeCompare(b);
}

function getMomentClickOrder(key: string) {
  if (/msg_click_rate/i.test(key)) return { group: 0, index: 0, subGroup: 0 };

  const buttonMatch = key.match(/msg_click_button(\d+)/i);
  if (buttonMatch) return { group: 10, index: Number(buttonMatch[1]), subGroup: 0 };

  const listMatch = key.match(/msg_click_list(\d+)/i);
  if (listMatch) return { group: 20, index: Number(listMatch[1]), subGroup: 0 };

  const catalogMatch = key.match(/msg_click_catalog_item(\d+)/i);
  if (catalogMatch) return { group: 30, index: Number(catalogMatch[1]), subGroup: 0 };

  if (/msg_click_media/i.test(key)) return { group: 40, index: 0, subGroup: 0 };
  if (/msg_click_title/i.test(key)) return { group: 41, index: 0, subGroup: 0 };
  if (/msg_click_text/i.test(key)) return { group: 42, index: 0, subGroup: 0 };

  const carouselButtonMatch = key.match(/msg_click_carousel_button(\d+)/i);
  if (carouselButtonMatch) return { group: 50, index: Number(carouselButtonMatch[1]), subGroup: 0 };

  const carouselShareMatch = key.match(/msg_click_carousel_share(\d+)/i);
  if (carouselShareMatch) return { group: 50, index: Number(carouselShareMatch[1]), subGroup: 1 };

  const carouselCouponMatch = key.match(/msg_click_carousel(\d+)_coupon/i);
  if (carouselCouponMatch) return { group: 50, index: Number(carouselCouponMatch[1]), subGroup: 2 };

  if (/msg_click_coupon/i.test(key)) return { group: 60, index: 0, subGroup: 0 };
  if (/msg_click_share/i.test(key)) return { group: 70, index: 0, subGroup: 0 };
  if (/msg_click_more/i.test(key)) return { group: 80, index: 0, subGroup: 0 };
  if (/msg_click_others/i.test(key)) return { group: 95, index: 0, subGroup: 0 };

  return { group: 99, index: 0, subGroup: 0 };
}

function resolveShareClickCount(messageType: MomentMessageType, metricMap: Record<string, unknown>, buttonCount: number) {
  const shareCount = Number(metricMap.msg_click_share ?? 0);
  const button1Count = Number(metricMap.msg_click_button1 ?? 0);
  const button2Count = Number(metricMap.msg_click_button2 ?? 0);

  if (buttonCount === 0) {
    return shareCount || button1Count;
  }

  if (messageType === "WIDE_MESSAGE" || messageType === "WIDE_LIST_MESSAGE") {
    return button2Count || shareCount;
  }

  return shareCount || button2Count;
}
